// noinspection JSUnusedGlobalSymbols

'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { IoMenuOutline as MenuIcon } from 'react-icons/io5';

import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

type SidebarContextProps = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      // This sets the cookie to keep the sidebar state.
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open]
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  // Adds a keyboard shortcut to toggle the sidebar.
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? 'expanded' : 'collapsed';

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot='sidebar-wrapper'
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            'ci:group/sidebar-wrapper ci:has-data-[variant=inset]:bg-sidebar ci:flex ci:min-h-svh ci:w-full',
            className
          )}
          {...props}>
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === 'none') {
    return (
      <div
        data-slot='sidebar'
        className={cn(
          'ci:bg-sidebar ci:text-sidebar-foreground ci:flex ci:h-full ci:w-(--sidebar-width) ci:flex-col',
          className
        )}
        {...props}>
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar='sidebar'
          data-slot='sidebar'
          data-mobile='true'
          className='ci:bg-sidebar ci:text-sidebar-foreground ci:w-(--sidebar-width) ci:p-0 ci:[&>button]:hidden'
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}>
          <SheetHeader className='ci:sr-only'>
            <SheetTitle>Sidebar</SheetTitle>
            <SheetDescription>Displays the mobile sidebar.</SheetDescription>
          </SheetHeader>
          <div className='ci:flex ci:h-full ci:w-full ci:flex-col'>{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      className='ci:group ci:peer ci:text-sidebar-foreground ci:hidden ci:md:block'
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
      data-slot='sidebar'>
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot='sidebar-gap'
        className={cn(
          'ci:relative ci:w-(--sidebar-width) ci:bg-transparent ci:transition-[width] ci:duration-200 ci:ease-linear',
          'ci:group-data-[collapsible=offcanvas]:w-0',
          'ci:group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'ci:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'ci:group-data-[collapsible=icon]:w-(--sidebar-width-icon)'
        )}
      />
      <div
        data-slot='sidebar-container'
        className={cn(
          'ci:fixed ci:inset-y-0 ci:z-10 ci:hidden ci:h-svh ci:w-(--sidebar-width) ci:transition-[left,right,width] ci:duration-200 ci:ease-linear ci:md:flex',
          side === 'left'
            ? 'ci:left-0 ci:group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
            : 'ci:right-0 ci:group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          // Adjust the padding for floating and inset variants.
          variant === 'floating' || variant === 'inset'
            ? 'ci:p-2 ci:group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
            : 'ci:group-data-[collapsible=icon]:w-(--sidebar-width-icon) ci:group-data-[side=left]:border-r ci:group-data-[side=right]:border-l',
          className
        )}
        {...props}>
        <div
          data-sidebar='sidebar'
          data-slot='sidebar-inner'
          className='ci:bg-sidebar ci:group-data-[variant=floating]:border-sidebar-border ci:flex ci:h-full ci:w-full ci:flex-col ci:group-data-[variant=floating]:rounded-lg ci:group-data-[variant=floating]:border ci:group-data-[variant=floating]:shadow-sm'>
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarTrigger({ className, onClick, ...props }: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();

  return (
    <Button
      data-sidebar='trigger'
      data-slot='sidebar-trigger'
      variant='ghost'
      size='icon'
      className={cn('ci:size-7', className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}>
      <MenuIcon className={'ci:size-5'} />
      <span className='ci:sr-only'>Toggle Sidebar</span>
    </Button>
  );
}

function SidebarRail({ className, ...props }: React.ComponentProps<'button'>) {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      data-sidebar='rail'
      data-slot='sidebar-rail'
      aria-label='Toggle Sidebar'
      tabIndex={-1}
      onClick={toggleSidebar}
      title='Toggle Sidebar'
      className={cn(
        'ci:hover:after:bg-sidebar-border ci:absolute ci:inset-y-0 ci:z-20 ci:hidden ci:w-4 ci:-translate-x-1/2 ci:transition-all ci:ease-linear ci:group-data-[side=left]:-right-4 ci:group-data-[side=right]:left-0 ci:after:absolute ci:after:inset-y-0 ci:after:left-1/2 ci:after:w-[2px] ci:sm:flex',
        'ci:in-data-[side=left]:cursor-w-resize ci:in-data-[side=right]:cursor-e-resize',
        'ci:[[data-side=left][data-state=collapsed]_&]:cursor-e-resize ci:[[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'ci:hover:group-data-[collapsible=offcanvas]:bg-sidebar ci:group-data-[collapsible=offcanvas]:translate-x-0 ci:group-data-[collapsible=offcanvas]:after:left-full',
        'ci:[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        'ci:[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className
      )}
      {...props}
    />
  );
}

function SidebarInset({ className, ...props }: React.ComponentProps<'main'>) {
  return (
    <main
      data-slot='sidebar-inset'
      className={cn(
        'ci:bg-background ci:relative ci:flex ci:w-full ci:flex-1 ci:flex-col',
        'ci:md:peer-data-[variant=inset]:m-2 ci:md:peer-data-[variant=inset]:ml-0 ci:md:peer-data-[variant=inset]:rounded-xl ci:md:peer-data-[variant=inset]:shadow-sm ci:md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2',
        className
      )}
      {...props}
    />
  );
}

function SidebarInput({ className, ...props }: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot='sidebar-input'
      data-sidebar='input'
      className={cn('ci:bg-background ci:h-8 ci:w-full ci:shadow-none', className)}
      {...props}
    />
  );
}

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-header'
      data-sidebar='header'
      className={cn('ci:flex ci:flex-col ci:gap-2 ci:p-2', className)}
      {...props}
    />
  );
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-footer'
      data-sidebar='footer'
      className={cn('ci:flex ci:flex-col ci:gap-2 ci:p-2 ci:pb-6', className)}
      {...props}
    />
  );
}

function SidebarSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot='sidebar-separator'
      data-sidebar='separator'
      className={cn('ci:bg-sidebar-border ci:mx-2 ci:w-auto', className)}
      {...props}
    />
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-content'
      data-sidebar='content'
      className={cn(
        'ci:flex ci:min-h-0 ci:flex-1 ci:flex-col ci:gap-2 ci:overflow-auto ci:group-data-[collapsible=icon]:overflow-hidden',
        className
      )}
      {...props}
    />
  );
}

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-group'
      data-sidebar='group'
      className={cn('ci:relative ci:flex ci:w-full ci:min-w-0 ci:flex-col ci:p-2', className)}
      {...props}
    />
  );
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot='sidebar-group-label'
      data-sidebar='group-label'
      className={cn(
        'ci:text-sidebar-foreground/70 ci:ring-sidebar-ring ci:flex ci:h-8 ci:shrink-0 ci:items-center ci:rounded-md ci:px-2 ci:text-xs ci:font-medium ci:outline-hidden ci:transition-[margin,opacity] ci:duration-200 ci:ease-linear ci:focus-visible:ring-2 ci:[&>svg]:size-4 ci:[&>svg]:shrink-0',
        'ci:group-data-[collapsible=icon]:-mt-8 ci:group-data-[collapsible=icon]:opacity-0',
        className
      )}
      {...props}
    />
  );
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot='sidebar-group-action'
      data-sidebar='group-action'
      className={cn(
        'ci:text-sidebar-foreground ci:ring-sidebar-ring ci:hover:bg-sidebar-accent ci:hover:text-sidebar-accent-foreground ci:absolute ci:top-3.5 ci:right-3 ci:flex ci:aspect-square ci:w-5 ci:items-center ci:justify-center ci:rounded-md ci:p-0 ci:outline-hidden ci:transition-transform ci:focus-visible:ring-2 ci:[&>svg]:size-4 ci:[&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'ci:after:absolute ci:after:-inset-2 ci:md:after:hidden',
        'ci:group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  );
}

function SidebarGroupContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-group-content'
      data-sidebar='group-content'
      className={cn('ci:w-full ci:text-sm', className)}
      {...props}
    />
  );
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot='sidebar-menu'
      data-sidebar='menu'
      className={cn('ci:flex ci:w-full ci:min-w-0 ci:flex-col ci:gap-1', className)}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot='sidebar-menu-item'
      data-sidebar='menu-item'
      className={cn('ci:group/menu-item ci:relative', className)}
      {...props}
    />
  );
}

const sidebarMenuButtonVariants = cva(
  'ci:peer/menu-button ci:flex ci:w-full ci:items-center ci:gap-2 ci:overflow-hidden ci:rounded-md ci:p-2 ci:text-left ci:text-sm ci:outline-hidden ci:ring-sidebar-ring ci:transition-[width,height,padding] ci:hover:bg-sidebar-accent ci:hover:text-sidebar-accent-foreground ci:focus-visible:ring-2 ci:active:bg-sidebar-accent ci:active:text-sidebar-accent-foreground ci:disabled:pointer-events-none ci:disabled:opacity-50 ci:group-has-data-[sidebar=menu-action]/menu-item:pr-8 ci:aria-disabled:pointer-events-none ci:aria-disabled:opacity-50 ci:data-[active=true]:bg-sidebar-accent ci:data-[active=true]:font-medium ci:data-[active=true]:text-sidebar-accent-foreground ci:data-[state=open]:hover:bg-sidebar-accent ci:data-[state=open]:hover:text-sidebar-accent-foreground ci:group-data-[collapsible=icon]:size-8! ci:group-data-[collapsible=icon]:p-2! ci:[&>span:last-child]:truncate ci:[&>svg]:size-4 ci:[&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'ci:hover:bg-sidebar-accent ci:hover:text-sidebar-accent-foreground',
        outline:
          'ci:bg-background ci:shadow-[0_0_0_1px_hsl(var(--sidebar-border))] ci:hover:bg-sidebar-accent ci:hover:text-sidebar-accent-foreground ci:hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
      },
      size: {
        default: 'ci:h-8 ci:text-sm',
        sm: 'ci:h-7 ci:text-xs',
        lg: 'ci:h-12 ci:text-sm ci:group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild ? Slot : 'button';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      data-slot='sidebar-menu-button'
      data-sidebar='menu-button'
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side='right'
        align='center'
        hidden={state !== 'collapsed' || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
}

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot='sidebar-menu-action'
      data-sidebar='menu-action'
      className={cn(
        'ci:text-sidebar-foreground ci:ring-sidebar-ring ci:hover:bg-sidebar-accent ci:hover:text-sidebar-accent-foreground ci:peer-hover/menu-button:text-sidebar-accent-foreground ci:absolute ci:top-1.5 ci:right-1 ci:flex ci:aspect-square ci:w-5 ci:items-center ci:justify-center ci:rounded-md ci:p-0 ci:outline-hidden ci:transition-transform ci:focus-visible:ring-2 ci:[&>svg]:size-4 ci:[&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'ci:after:absolute ci:after:-inset-2 ci:md:after:hidden',
        'ci:peer-data-[size=sm]/menu-button:top-1',
        'ci:peer-data-[size=default]/menu-button:top-1.5',
        'ci:peer-data-[size=lg]/menu-button:top-2.5',
        'ci:group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'ci:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground ci:group-focus-within/menu-item:opacity-100 ci:group-hover/menu-item:opacity-100 ci:data-[state=open]:opacity-100 ci:md:opacity-0',
        className
      )}
      {...props}
    />
  );
}

function SidebarMenuBadge({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sidebar-menu-badge'
      data-sidebar='menu-badge'
      className={cn(
        'ci:text-sidebar-foreground ci:pointer-events-none ci:absolute ci:right-1 ci:flex ci:h-5 ci:min-w-5 ci:items-center ci:justify-center ci:rounded-md ci:px-1 ci:text-xs ci:font-medium ci:tabular-nums ci:select-none',
        'ci:peer-hover/menu-button:text-sidebar-accent-foreground ci:peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
        'ci:peer-data-[size=sm]/menu-button:top-1',
        'ci:peer-data-[size=default]/menu-button:top-1.5',
        'ci:peer-data-[size=lg]/menu-button:top-2.5',
        'ci:group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  );
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  showIcon?: boolean;
}) {
  // Random width between 50 to 90%.
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div
      data-slot='sidebar-menu-skeleton'
      data-sidebar='menu-skeleton'
      className={cn('ci:flex ci:h-8 ci:items-center ci:gap-2 ci:rounded-md ci:px-2', className)}
      {...props}>
      {showIcon && (
        <Skeleton className='ci:size-4 ci:rounded-md' data-sidebar='menu-skeleton-icon' />
      )}
      <Skeleton
        className='ci:h-4 ci:max-w-(--skeleton-width) ci:flex-1'
        data-sidebar='menu-skeleton-text'
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot='sidebar-menu-sub'
      data-sidebar='menu-sub'
      className={cn(
        'ci:border-sidebar-border ci:mx-3.5 ci:flex ci:min-w-0 ci:translate-x-px ci:flex-col ci:gap-1 ci:border-l ci:px-2.5 ci:py-0.5',
        'ci:group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  );
}

function SidebarMenuSubItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot='sidebar-menu-sub-item'
      data-sidebar='menu-sub-item'
      className={cn('ci:group/menu-sub-item ci:relative', className)}
      {...props}
    />
  );
}

function SidebarMenuSubButton({
  asChild = false,
  size = 'md',
  isActive = false,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
}) {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      data-slot='sidebar-menu-sub-button'
      data-sidebar='menu-sub-button'
      data-size={size}
      data-active={isActive}
      className={cn(
        'ci:text-sidebar-foreground ci:ring-sidebar-ring ci:hover:bg-sidebar-accent ci:hover:text-sidebar-accent-foreground ci:active:bg-sidebar-accent ci:active:text-sidebar-accent-foreground ci:[&>svg]:text-sidebar-accent-foreground ci:flex ci:h-7 ci:min-w-0 ci:-translate-x-px ci:items-center ci:gap-2 ci:overflow-hidden ci:rounded-md ci:px-2 ci:outline-hidden ci:focus-visible:ring-2 ci:disabled:pointer-events-none ci:disabled:opacity-50 ci:aria-disabled:pointer-events-none ci:aria-disabled:opacity-50 ci:[&>span:last-child]:truncate ci:[&>svg]:size-4 ci:[&>svg]:shrink-0',
        'ci:data-[active=true]:bg-sidebar-accent ci:data-[active=true]:text-sidebar-accent-foreground',
        size === 'sm' && 'ci:text-xs',
        size === 'md' && 'ci:text-sm',
        'ci:group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  // eslint-disable-next-line react-refresh/only-export-components
  useSidebar,
};
